import { Component, type ReactNode, type ErrorInfo } from 'react';
import { Button, Card, CardContent } from './ui';
import { AlertTriangle } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex min-h-screen items-center justify-center bg-background p-4">
          <Card className="max-w-md text-center">
            <CardContent className="space-y-4 pt-6">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
                <AlertTriangle className="h-6 w-6 text-destructive" />
              </div>
              <h2 className="text-xl font-bold">
                Algo ha ido mal
              </h2>
              <p className="text-muted-foreground">
                Ha ocurrido un error inesperado. Por favor, intentalo de nuevo.
              </p>
              {this.state.error && (
                <pre className="overflow-auto rounded bg-muted p-2 text-left text-xs text-destructive">
                  {this.state.error.message}
                </pre>
              )}
              <div className="flex justify-center gap-2">
                <Button onClick={this.handleRetry}>Reintentar</Button>
                <Button variant="outline" onClick={() => window.location.reload()}>
                  Recargar pagina
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
